import { FC, useContext, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  ContextMenu,
  IContextMenuItem,
  Table,
  useContextMenu,
} from '@smartshell/ui-kit';

import { LayoutContext } from 'components/Layout';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import { TDiscount } from 'types';

import { DiscountManager } from './DiscountManager';
import s from './Discounts.module.scss';
import { DELETE_DISCOUNT, GET_DISCOUNTS } from './schema';
import { UseGetColumns } from './UseGetColumns';

export const Discounts: FC = () => {
  const { t } = useContext(LocalizationContext);
  const layoutElement = useContext(LayoutContext);

  const {
    data: loadedDiscounts,
    refetch,
    loading,
  } = useQuery<{
    discounts: { data: Array<TDiscount> };
  }>(GET_DISCOUNTS, { fetchPolicy: 'no-cache' });

  const [selectedDiscount, setSelectedDiscount] = useState<TDiscount | null>(
    null
  );
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setSelectedDiscount(null);
  }, [loadedDiscounts]);

  const {
    discounts: { data },
  } = loadedDiscounts ?? { discounts: { data: [] } };

  const {
    contextMenuData,
    closeContextMenu,
    cursorPosition,
    onContextMenu,
    onTouchEnd,
    onTouchMove,
    onTouchStart,
  } = useContextMenu<TDiscount>();

  const [deleteDiscount] = useMutation(DELETE_DISCOUNT, {
    onCompleted: () => {
      notification.info({
        t,
        title: t('discount.deleted'),
        type: 'success',
        dismiss: 5,
      });
      refetch();
    },
    fetchPolicy: 'no-cache',
  });

  const contextMenuItems: Array<IContextMenuItem<TDiscount>> = [
    {
      key: 'edit',
      text: t('edit'),
      iconName: 'EditIcon',
      onClick: (discount: TDiscount) => {
        setIsVisible(true);
        setSelectedDiscount(discount);
      },
    },
    {
      key: 'delete',
      className: s['remove-item'],
      text: t('delete'),
      iconName: 'BucketIcon',
      onClick: (discount: TDiscount) =>
        deleteDiscount({ variables: { id: discount.id } }).catch(console.error),
    },
  ];
  const COLUMNS = UseGetColumns();

  return (
    <div className={s['page-container']}>
      {contextMenuData && cursorPosition && (
        <ContextMenu
          layoutElement={layoutElement}
          data={contextMenuData}
          items={contextMenuItems}
          closeContextMenu={closeContextMenu}
          position={cursorPosition}
        />
      )}
      {isVisible && (
        <DiscountManager
          discount={selectedDiscount}
          onSuccess={() => {
            refetch();
            setIsVisible(false);
            setSelectedDiscount(null);
          }}
          onClose={() => {
            setSelectedDiscount(null);
            setIsVisible(false);
          }}
        />
      )}
      <div className={s['list']}>
        <Button
          iconName="CirclePlusIcon"
          className={s['add']}
          onClick={() => setIsVisible(true)}
        >
          {t('discounts.add')}
        </Button>
        <Table
          data={data}
          columns={COLUMNS}
          isLoading={loading}
          emptyDataPlaceholder={t('discounts.placeholder')}
          onContextMenu={(event, data) => {
            onContextMenu(event, data);
          }}
          onTouchStart={(event, row) => {
            onTouchStart(event, row);
          }}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className={s.table}
          onRowDoubleClick={(_, data) => {
            setSelectedDiscount(data);
            setIsVisible(true);
          }}
        />
      </div>
    </div>
  );
};
